import React, { useEffect, useRef } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { ChevronLeftIcon, ChevronRightIcon, XIcon } from 'lucide-react';
import type { VolumePage } from '../data/volumePages';
import { SamplePage } from './SamplePage';
type PageLightboxProps = {
  pages: VolumePage[];
  index: number | null;
  onIndexChange: (index: number) => void;
  onClose: () => void;
};
const EASE = [0.23, 1, 0.32, 1] as const;
export function PageLightbox({
  pages,
  index,
  onIndexChange,
  onClose
}: PageLightboxProps) {
  const closeRef = useRef<HTMLButtonElement>(null);
  const swipe = useRef<{
    x: number;
    y: number;
  } | null>(null);
  const swiped = useRef(false);
  const reduceMotion = useReducedMotion();
  const open = index !== null;
  const count = pages.length;
  const step = (dir: 1 | -1) => {
    if (index === null) return;
    onIndexChange((index + dir + count) % count);
  };
  useEffect(() => {
    if (!open) return;
    const previous = document.activeElement as HTMLElement | null;
    const overflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    closeRef.current?.focus();
    return () => {
      document.body.style.overflow = overflow;
      previous?.focus();
    };
  }, [open]);
  useEffect(() => {
    if (index === null) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();else if (e.key === 'ArrowRight') onIndexChange((index + 1) % count);else if (e.key === 'ArrowLeft') onIndexChange((index - 1 + count) % count);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [index, count, onClose, onIndexChange]);
  const onPointerDown = (e: React.PointerEvent) => {
    swipe.current = {
      x: e.clientX,
      y: e.clientY
    };
    swiped.current = false;
  };
  const onPointerUp = (e: React.PointerEvent) => {
    if (!swipe.current) return;
    const dx = e.clientX - swipe.current.x;
    const dy = e.clientY - swipe.current.y;
    swipe.current = null;
    if (Math.abs(dx) > 50 && Math.abs(dx) > Math.abs(dy)) {
      swiped.current = true;
      step(dx < 0 ? 1 : -1);
    }
  };
  const onBackdropClick = () => {
    if (swiped.current) {
      swiped.current = false;
      return;
    }
    onClose();
  };
  const page = index !== null ? pages[index] : null;
  const arrowClass = 'flex h-12 w-12 items-center justify-center rounded-full bg-hl text-hl-ink shadow-card transition-colors duration-150 hover:bg-hl-hover focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-chalk/70';
  return <AnimatePresence data-id="element-248" data-mp="w5xufi1n2sdl">
      {page && index !== null && <motion.div role="dialog" aria-modal="true" aria-label={`Page ${index + 1} of ${count}: ${page.caption}`} className="fixed inset-0 z-50 flex touch-pan-y select-none items-center justify-center bg-black/90" initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} transition={{
      duration: 0.2,
      ease: EASE
    }} onClick={onBackdropClick} onPointerDown={onPointerDown} onPointerUp={onPointerUp} onPointerCancel={() => swipe.current = null} data-id="element-249" data-mp="w7hb39r7j5fw">
          <p className="absolute left-4 top-4 flex h-11 items-center text-sm font-semibold tabular-nums text-chalk" aria-live="polite" data-id="element-250" data-mp="2164g6q7qwmk2">
            {index + 1} / {count}
          </p>

          <button ref={closeRef} type="button" onClick={e => {
        e.stopPropagation();
        onClose();
      }} aria-label="Close page preview" className="absolute right-4 top-4 flex h-11 w-11 items-center justify-center rounded-full bg-white/10 text-white transition-colors duration-150 hover:bg-white/20 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-hl" data-id="element-251" data-mp="2tujm8n8wfuj9">
            <XIcon className="h-5 w-5" aria-hidden="true" data-id="element-252" data-mp="3hjmcgjhlmvpd" />
          </button>

          <motion.div key={page.sample} onClick={e => e.stopPropagation()} className="lightbox-page overflow-hidden rounded-xl bg-chalk shadow-book" initial={reduceMotion ? false : {
        opacity: 0,
        scale: 0.96
      }} animate={{
        opacity: 1,
        scale: 1
      }} exit={reduceMotion ? {
        opacity: 0
      } : {
        opacity: 0,
        scale: 0.96
      }} transition={{
        duration: 0.2,
        ease: EASE
      }} data-id="element-253" data-mp="1turh0tf6bysr">
            {page.src ? <img src={page.src} alt={page.alt} width={1080} height={1920} draggable={false} className="lightbox-img block" data-id="element-254" data-mp="2cnbtj0akfrae" /> : <div role="img" aria-label={page.alt} className="w-[min(calc(100vw-2rem),960px)]" data-id="element-255" data-mp="22uo08fiso5gf">
                <SamplePage id={page.sample} data-id="element-256" data-mp="1xx4mrhtx7wpj" />
              </div>}
          </motion.div>

          <div className="absolute bottom-5 left-1/2 flex -translate-x-1/2 gap-6 sm:static sm:contents" data-id="element-257" data-mp="2vc0chgphnmjj">
            <button type="button" onClick={e => {
          e.stopPropagation();
          step(-1);
        }} aria-label="Previous page" className={`${arrowClass} sm:absolute sm:left-6 sm:top-1/2 sm:-translate-y-1/2`} data-id="element-258" data-mp="2qjz4w839mjzu">
              <ChevronLeftIcon className="h-6 w-6" aria-hidden="true" data-id="element-259" data-mp="37tkwhmw45zrv" />
            </button>
            <button type="button" onClick={e => {
          e.stopPropagation();
          step(1);
        }} aria-label="Next page" className={`${arrowClass} sm:absolute sm:right-6 sm:top-1/2 sm:-translate-y-1/2`} data-id="element-260" data-mp="3cugy1tszt9u4">
              <ChevronRightIcon className="h-6 w-6" aria-hidden="true" data-id="element-261" data-mp="3qlvm4f1rc181" />
            </button>
          </div>
        </motion.div>}
    </AnimatePresence>;
}